/**
 * Every handled-error code the TypeScript app can raise.
 *
 * This list exists so the presentation registry can be *exhaustive*: it is the
 * `Record` key set that `presentation.ts` must satisfy, which turns "someone
 * added an error code and never wrote the copy" from a runtime shrug into a
 * `pnpm typecheck` failure.
 *
 * Kept honest by `__tests__/codes.unit.test.ts`, which scans `src/`, `ee/` and
 * the workspace `packages/` for every code a `HandledError` subclass declares
 * and fails when this list and the code drift apart in either direction. All
 * three trees raise handled errors, and a scan that covers only one is a list
 * that silently stops being exhaustive. Adding a code here without
 * throwing it anywhere is just as much a bug as the reverse — it means dead
 * copy, which is how `recipient_not_in_team` survived in the automations
 * explainer for a code nothing raises.
 *
 * Codes declared by the Go services are NOT here — they come from
 * `goErrorCodes` in `@langwatch/handled-error`, generated by `cmd/herrgen`.
 *
 * Two entries are SCREAMING_CASE rather than the usual snake_case:
 * `LAST_WAY_IN` and `MFA_REQUIRED_BY_ORGANIZATION` are better-auth's own
 * `APIError` codes, thrown by `LastWayInGuard`
 * (`src/server/better-auth/last-way-in.ts`) on the mounted
 * `/passkey/delete-passkey` and `/two-factor/disable` routes. Neither path is
 * in `HANDLED_BY_BETTER_AUTH_CODE` (`src/server/better-auth/handled-errors.ts`),
 * so the code reaches the client exactly as better-auth wrote it — not
 * translated into one of our own `HandledError` subclasses. They are listed
 * here, spelled the way the wire actually sends them, so the presentation
 * registry has copy for them; see `codes.unit.test.ts`'s
 * `BETTER_AUTH_PASSTHROUGH_CODES` for why the usual raised/listed scan cannot
 * see them.
 */
export const APP_ERROR_CODES = [
  "LAST_WAY_IN",
  "MFA_REQUIRED_BY_ORGANIZATION",
  "agent_busy",
  "agent_call_failed",
  "agent_call_timeout",
  "agent_dev_tunnel_unreachable",
  "agent_disconnected",
  "agent_environment_unresolved",
  "agent_instance_lost",
  "agent_listing_unavailable",
  "agent_not_found",
  "agent_offline",
  "agent_owner_only",
  "agent_parameter_invalid",
  "agent_payload_too_large",
  "agent_register_only",
  "agent_register_refused",
  "agent_report_rate_limited",
  "agent_session_unknown",
  "agent_test_refused",
  "ai_call_failed",
  "ai_query_provider_error",
  "already_organization_member",
  "anomaly_rule_not_found",
  "api_key_already_revoked",
  "api_key_not_found",
  "api_key_not_in_organization",
  "api_key_not_owned",
  "api_key_permission_denied",
  "api_key_permission_not_delegable",
  "api_key_reserved_name",
  "api_key_scope_violation",
  "auth_direct_registration_unavailable",
  "auth_no_address_to_confirm",
  "auth_rate_limited",
  "authz_grant_not_confirmed",
  "authz_ledger_unavailable",
  "avatar_image_processing_failed",
  "avatar_image_too_large",
  "avatar_image_type_unsupported",
  "avatar_image_unreadable",
  "avatar_rate_limited",
  "billing_currency_unsupported",
  "billing_customer_deleted",
  "billing_customer_email_required",
  "billing_plan_price_missing",
  "billing_provider_unavailable",
  "billing_quote_expired",
  "broadcaster_not_active",
  "budget_not_found",
  "cache_entry_not_found",
  "cache_rule_not_found",
  "cannot_demote_last_admin",
  "cannot_disable_last_admin",
  "cannot_disable_self",
  "cannot_impersonate_admin",
  "cannot_impersonate_deactivated_user",
  "cannot_impersonate_without_second_factor",
  "cannot_reimpersonate_while_impersonating",
  "cannot_remove_last_admin",
  "cannot_remove_self",
  "cannot_remove_self_as_last_admin",
  "cli_key_selection_invalid",
  "clickhouse_overloaded",
  "clickhouse_unavailable",
  "codex_auth_failed",
  "contested_credentials",
  "credential_class_mismatch",
  "custom_chart_playground_not_enabled",
  "custom_graph_writes_disabled_for_playground",
  "custom_role_id_required",
  "custom_role_in_use",
  "custom_role_name_reserved",
  "custom_role_name_taken",
  "custom_role_not_assignable",
  "custom_role_not_found",
  "dashboard_access_restricted",
  "dashboard_widget_definition_invalid",
  "dashboard_widget_not_found",
  "dataset_column_type_change_unsupported",
  "dataset_name_taken",
  "dataset_not_ready",
  "dataset_stale_columns",
  "dataset_too_large_to_search",
  "dspy_step_not_found",
  "duplicate_invite",
  "email_already_registered",
  "enterprise_plan_required",
  "evaluation_not_found",
  "evaluator_category_in_use",
  "evaluator_category_name_taken",
  "evaluator_category_not_found",
  "evaluator_config_error",
  "evaluator_execution_error",
  "evaluator_input_too_large",
  "evaluator_missing_field",
  "evaluator_no_inputs_resolved",
  "evaluator_not_found",
  "evaluator_sample_already_added",
  "evaluator_sample_not_found",
  "experiment_invalid_workbench_state",
  "experiment_not_found",
  "experiment_stale_workbench_state",
  "experiment_type_mismatch",
  "experiment_version_not_found",
  "experiment_workbench_missing_reference",
  "export_failed",
  "external_id_conflict",
  "filter_field_unknown",
  "filter_parse_error",
  "gateway_budget_cycle_anchor_invalid",
  "gateway_budget_not_found",
  "gateway_budget_scope_unreachable",
  "gateway_group_budget_unsupported",
  "gateway_guardrail_project_mismatch",
  "gateway_provider_bindings_gone",
  "gateway_scope_org_mismatch",
  "gateway_spend_group_by_unstable",
  "gateway_spend_unavailable",
  "gateway_trace_project_ambiguous",
  "gateway_trace_project_unknown",
  "github_installation_suspended",
  "github_not_connected",
  "github_pr_not_mapped",
  "github_rate_limited",
  "github_repo_not_accessible",
  "grant_validation_failed",
  "group_member_already_added",
  "group_not_found",
  "group_not_in_organization",
  "guardrail_attach_forbidden",
  "health_check_failed",
  "idempotency_error",
  "identity_already_linked",
  "identity_detach_strands_user",
  "identity_email_in_use",
  "identity_erased",
  "identity_identifier_already_held",
  "identity_identifier_not_found",
  "identity_identifier_not_verifiable",
  "identity_jit_disabled",
  "identity_link_proposal_not_found",
  "identity_link_proposal_resolved",
  "identity_link_proposed",
  "identity_match_suggestion_not_found",
  "identity_mfa_backup_codes_exhausted",
  "identity_mfa_code_invalid",
  "identity_mfa_enrollment_expired",
  "identity_mfa_enrollment_required",
  "identity_mfa_locked_out",
  "identity_mfa_password_invalid",
  "identity_mfa_required_by_organization",
  "identity_mfa_requirement_not_licensed",
  "identity_passkey_already_registered",
  "identity_passkey_ceremony_failed",
  "identity_passkey_not_recognized",
  "identity_password_rejected",
  "identity_primary_must_demote_first",
  "identity_primary_requires_verified",
  "identity_reset_link_invalid",
  "identity_sign_in_refused",
  "identity_unsupported_storage_query",
  "identity_verification_expired",
  "identity_verification_invalid",
  "identity_verification_not_settled",
  "impersonation_cannot_change_credentials",
  "ingestion_key_not_found",
  "ingestion_key_revoke_incomplete",
  "ingestion_key_session_revoked",
  "ingestion_key_source_not_allowed",
  "ingestion_key_workspace_missing",
  "ingestion_source_cap_reached",
  "ingestion_source_not_found",
  "insufficient_permissions",
  // Also a Go code, with copy already written under the shared/transport
  // heading — the REST boundary in `packages/api` puts it on the wire as the
  // code for an unhandled failure, so the scan sees it here too.
  "internal_error",
  "invalid_action_params",
  "invalid_credentials",
  "invalid_cursor",
  "invalid_email_recipient",
  "invalid_experiment_configuration",
  "invite_expired",
  "invite_not_found",
  "invite_throttled",
  "invite_wrong_account",
  "join_auto_connection_admits",
  "join_auto_domain_unproven",
  "join_auto_not_licensed",
  "join_not_available",
  "join_request_already_pending",
  "join_request_not_found",
  "join_request_not_pending",
  "join_request_throttled",
  "langy_agent_at_capacity",
  "langy_agent_errored",
  "langy_agent_session_lost",
  "langy_agent_unavailable",
  "langy_api_actor_missing",
  "langy_api_credential_invalid",
  "langy_api_credential_missing",
  "langy_api_key_no_langy_access",
  "langy_api_key_unowned",
  "langy_api_request_invalid",
  "langy_codex_plan_limit",
  "langy_conversation_id_unadoptable",
  "langy_conversation_not_found",
  "langy_conversation_not_owned",
  "langy_credential_resolution",
  "langy_dispatch_rejected",
  "langy_egress_misconfigured",
  "langy_empty_message",
  "langy_github_not_connected",
  "langy_github_repo_not_accessible",
  "langy_idempotency_mismatch",
  "langy_insufficient_scope",
  "langy_local_permission_timeout",
  "langy_local_request_expired",
  "langy_local_request_invalid",
  "langy_local_skip_model_not_allowed",
  "langy_local_workspace_offline",
  "langy_model_not_allowed",
  "langy_model_not_configured",
  "langy_model_unavailable",
  "langy_not_enabled",
  "langy_rate_limited",
  "langy_skill_not_available",
  "langy_turn_in_progress",
  "langy_turn_not_stoppable",
  "langy_turn_timeout",
  "langy_ui_action_unknown",
  "langy_ui_experiment_required",
  "langy_ui_handler_failed",
  "langy_ui_no_browser",
  "langy_ui_page_out_of_date",
  "langy_ui_payload_invalid",
  "langy_ui_save_failed",
  "langy_ui_timeout",
  "langy_ui_turn_inactive",
  "langy_wait_expired",
  "langy_worker_restarting",
  "langy_worker_spawn_failed",
  "langy_worker_stopped",
  "license_expired",
  "license_key_invalid",
  "license_signing_failed",
  "license_signing_key_encrypted",
  "license_signing_key_not_pem",
  "lite_member_restricted",
  "lite_member_viewer_only",
  "lwql_granularity_parameter_type",
  "lwql_granularity_requires_window",
  "lwql_granularity_too_fine",
  "lwql_not_enabled",
  "lwql_not_permitted",
  "lwql_parameter_missing",
  "lwql_provisioning_incomplete",
  "lwql_reserved_parameter_supplied",
  "lwql_reserved_parameter_type",
  "lwql_unavailable",
  "lwql_unknown_identifier",
  "lwql_unparseable",
  "malformed_custom_role_permissions",
  "malformed_request",
  "member_not_found",
  "member_seat_limit_reached",
  "membership_disabled",
  "migration_enrolled_automatically",
  "migration_enrollment_already_exists",
  "migration_enrollment_cloud_only",
  "migration_enrollment_not_found",
  "migration_not_available_on_installation",
  "migration_pass_already_running",
  "migration_rollback_blocked_by_dependent",
  "migration_rollback_cutover_not_started",
  "migration_run_requires_enrollment",
  "migration_unknown",
  "missing_annotator",
  "missing_credentials",
  // Not declared by a subclass anywhere: `nlpgo/goHandledError.ts` promotes a
  // relayed Go error's `meta.reason` to the code, so this one is minted at the
  // boundary rather than thrown. It still reaches a customer, so it still needs
  // copy — see RELAYED_META_CODES in `__tests__/codes.unit.test.ts`.
  "missing_provider",
  "missing_slack_bot_token",
  "missing_slack_webhook",
  "model_default_scope_forbidden",
  "model_default_user_key_required",
  "model_not_configured",
  "model_provider_anchor_required",
  "model_provider_credentials_unreadable",
  "model_provider_credentials_would_be_dropped",
  "model_provider_deprecated",
  "model_provider_disabled",
  "model_provider_not_found",
  "model_provider_routing_handle_invalid",
  "model_provider_routing_handle_taken",
  "model_provider_scope_forbidden",
  "model_provider_scopes_required",
  "model_provider_skip_permissions_pattern_invalid",
  "model_provider_test_rate_limited",
  "model_restricted_for_feature",
  "monitor_evaluator_required",
  "no_admin_configured",
  // Also a Go code, with copy already written under the shared/transport
  // heading — `ee/admin/routes/admin.ts` raises it to hide the admin surface
  // from a session that is no longer an admin.
  "not_found",
  "notification_delivery_error",
  "offboard_incomplete",
  "org_exclusive_permission_scope",
  "organization_not_found",
  "organization_not_found_for_team",
  "organization_slug_taken",
  "page_too_deep",
  "permission_denied",
  "personal_project_key_required",
  "personal_usage_key_mismatch",
  "personal_workspace_not_managed_here",
  "project_not_found",
  "project_permission_denied",
  "project_visibility_too_wide",
  "prompt_not_found",
  "provider_endpoint_redirected",
  "provider_key_invalid",
  "provider_key_missing",
  "provider_key_restricted",
  "provider_refused",
  "provider_service_disabled",
  "provider_unreachable",
  "published_workflow_version_not_found",
  "qa_call_not_found",
  "qa_policy_bands_invalid",
  "qa_policy_draft_missing",
  "qa_policy_duplicate_monitor",
  "qa_policy_invalid",
  "qa_policy_monitor_not_found",
  "qa_policy_version_conflict",
  "qa_policy_version_not_found",
  "qa_policy_weights_invalid",
  "qa_widget_dashboard_not_found",
  "qa_widget_definition_invalid",
  "qa_widget_not_found",
  "query_memory_exceeded",
  "query_scan_limit_exceeded",
  "query_timeout",
  "resource_limit_exceeded",
  "role_binding_already_exists",
  "role_binding_not_found",
  "role_binding_not_project_scoped",
  "role_binding_principal_invalid",
  "rum_ingest_disabled",
  "rum_payload_invalid",
  "rum_payload_too_large",
  "rum_rate_limited",
  "run_not_found",
  "saved_workbench_chart_already_exists",
  "saved_workbench_chart_dashboard_not_found",
  "saved_workbench_chart_definition_invalid",
  "saved_workbench_chart_not_found",
  "saved_workbench_chart_specification_refused",
  "saved_workbench_charts_disabled_for_playground",
  "scenario_field_type_invalid",
  "scenario_field_unknown",
  "scenario_not_found",
  "scenario_parameter_missing",
  "scenario_parameter_option_invalid",
  "scenario_parameter_required",
  "scenario_parameter_template_invalid",
  "scenario_parameter_unknown",
  "scenario_reserved_set_id",
  "scenario_run_export_forbidden",
  "scenario_run_export_unauthenticated",
  "scenario_secret_parameter_conflict",
  "scenario_secret_parameter_in_text",
  "scenario_secret_parameter_missing",
  "scenario_stale_version",
  "scenario_test_suite_not_found",
  "scenario_version_not_found",
  "schedule_already_in_flight",
  "schedule_inactive",
  "schedule_not_found",
  "schedule_run_in_progress",
  "schedule_slot_not_stale",
  "schema_failure",
  "scim_connection_not_found",
  "scim_connection_required",
  "scim_managed_group",
  "scim_token_not_found",
  "scim_write_outside_connection",
  "scope_not_in_organization",
  "seat_billing_unavailable",
  "session_is_current",
  "share_link_exhausted",
  "share_link_expired",
  "share_link_forbidden",
  "share_link_not_found",
  "share_read_rate_limited",
  "span_not_found",
  "spend_source_unavailable",
  "sso_connection_activation_blocked",
  "sso_connection_domain_taken",
  "sso_connection_invalid_transition",
  "sso_connection_issuer_not_public",
  "sso_connection_operator_act_required",
  "sso_connection_string_edit_retired",
  "sso_connection_teardown_strands_users",
  "sso_saml_not_self_serve",
  "storage_not_writable",
  "subscription_ambiguous",
  "subscription_not_linked",
  "subscription_service_unavailable",
  "subscription_sync_failed",
  "suite_all_scenarios_archived",
  "suite_all_targets_archived",
  "suite_evaluator_mapping_invalid",
  "suite_evaluator_mappings_missing",
  "suite_evaluator_not_found",
  "suite_field_identifier_duplicate",
  "suite_field_identifier_invalid",
  "suite_field_in_use",
  "suite_invalid_scenario_references",
  "suite_invalid_target_references",
  "suite_name_taken",
  "suite_not_found",
  "suite_scope_empty",
  "suite_scope_not_allowed",
  "suite_targets_required",
  "system_prompt_conflict",
  "system_prompt_required",
  "task_not_found",
  "team_last_admin_required",
  "team_member_already_added",
  "team_membership_not_found",
  "team_name_taken",
  "team_not_found",
  "team_not_in_organization",
  "template_immutable",
  "template_not_found",
  "template_validation_error",
  "test_fire_unavailable",
  "time_range_too_wide",
  "trace_not_evaluatable",
  "trace_not_found",
  "trace_project_required",
  "trace_sharing_disabled",
  "trigger_filters_required",
  // Also a Go code, with copy already written under the shared/transport
  // heading — an app-level subclass raises it too (`ee/admin/routes/admin.ts`),
  // and the scan is right to insist it be listed here as well.
  "unauthorized",
  "usage_report_failed",
  "user_not_in_organization",
  "user_to_impersonate_not_found",
  // Raised by the shared package rather than an app-level subclass:
  // `ValidationError` (and `ValidationError.fromZodError`, which the tRPC
  // boundary uses to promote input ZodErrors onto the handled channel).
  "validation_error",
  "virtual_key_expiry_in_past",
  "virtual_key_not_found",
  "voice_agents_disabled",
  "voice_conversation_mismatch",
  "voice_key_missing",
  "voice_mint_failed",
  "voice_name_required",
  "voice_phone_transport_unavailable",
  "voice_recording_key_missing",
  "voice_recording_unavailable",
  "voice_session_invalid",
  // The webhook platform names its two endpoint failures itself, in
  // `~/app/api/webhooks/[[...route]]/error-handler`, rather than deriving
  // them from the status: `not_found` alone would not say WHICH lookup
  // missed, and an endpoint id is the only one those routes take.
  "webhook_endpoint_invalid",
  "webhook_endpoint_not_found",
  "webhook_event_not_found",
  "workflow_execution_failed",
  "workflow_not_found",
] as const;

export type AppErrorCode = (typeof APP_ERROR_CODES)[number];
